import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../api/client';
import { useAuth } from './useAuth';

export interface Vault {
  id: string;
  name: string;
  type: 'personal' | 'business' | 'shared';
  role: 'admin' | 'contributor' | 'consumer';
}

interface VaultContextType {
  vaults: Vault[];
  activeVaultId: string | null | 'shared';
  setActiveVaultId: (id: string | null | 'shared') => void;
  loading: boolean;
  refreshVaults: () => Promise<void>;
  createVault: (name: string, type: 'personal' | 'business' | 'shared') => Promise<Vault | null>;
  updateVault: (id: string, name: string) => Promise<boolean>;
  deleteVault: (id: string) => Promise<boolean>;
}

const VaultContext = createContext<VaultContextType | null>(null);

export function VaultProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const [vaults, setVaults] = useState<Vault[]>([]);
  const [activeVaultId, setActiveVaultId] = useState<string | null | 'shared'>(null);
  const [loading, setLoading] = useState(true);

  const refreshVaults = async () => {
    try {
      const data = await api.get('/api/vaults');
      if (data.vaults) {
        setVaults(data.vaults);
      }
    } catch (e) {
      console.error('Failed to load vaults', e);
    } finally {
      setLoading(false);
    }
  };

  const createVault = async (name: string, type: 'personal' | 'business' | 'shared') => {
    try {
      const data = await api.post('/api/vaults', { name, type });
      if (data.vault) {
        await refreshVaults();
        return data.vault;
      }
    } catch (e) {
      console.error('Failed to create vault', e);
    }
    return null;
  };

  const updateVault = async (id: string, name: string) => {
    try {
      await api.put(`/api/vaults/${id}`, { name });
      await refreshVaults();
      return true;
    } catch (e: any) {
      alert('Cập nhật tên Kho báu thất bại: ' + (e.message || 'Lỗi hệ thống'));
      return false;
    }
  };

  const deleteVault = async (id: string) => {
    try {
      await api.delete(`/api/vaults/${id}`);
      if (activeVaultId === id) setActiveVaultId(null);
      await refreshVaults();
      return true;
    } catch (e: any) {
      alert('Xóa Kho báu thất bại: ' + (e.message || 'Lỗi hệ thống'));
      return false;
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      refreshVaults();
    } else {
      setVaults([]);
      setActiveVaultId(null);
    }
  }, [isLoggedIn]);

  return (
    <VaultContext.Provider value={{ vaults, activeVaultId, setActiveVaultId, loading, refreshVaults, createVault, updateVault, deleteVault }}>
      {children}
    </VaultContext.Provider>
  );
}

export function useVaults() {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVaults must be used within VaultProvider');
  return ctx;
}
